import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { RootState } from "features/config/store";
import { GetOrderPaymentDetailsResponse } from "features/stock-ordering/core/domain/payment.model";
import {
  GetOrderPaymentDetailsRepository,
  GetOrderPaymentDetailsRepoResponse,
} from "features/stock-ordering/data/stock-ordering.repository";

export enum GetOrderPaymentDetailsState {
  initial,
  inProgress,
  success,
  fail,
}

interface InitialState {
  status: GetOrderPaymentDetailsState;
  message: string;
  data: GetOrderPaymentDetailsResponse | undefined;
}

const initialState: InitialState = {
  status: GetOrderPaymentDetailsState.initial,
  message: "",
  data: undefined,
};

export const getOrderPaymentDetails = createAsyncThunk(
  "getOrderPaymentDetails",
  async (orderId: number, { rejectWithValue }) => {
    try {
      const response: GetOrderPaymentDetailsRepoResponse =
        await GetOrderPaymentDetailsRepository(orderId);
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (!error.response) {
          throw error;
        }
        throw rejectWithValue(error.response.data.message);
      }
    }
  },
);

export const getOrderPaymentDetailsSlice = createSlice({
  name: "getOrderPaymentDetails",
  initialState,
  reducers: {
    resetGetOrderPaymentDetails: (state) => {
      state.status = GetOrderPaymentDetailsState.initial;
      state.data = undefined;
      state.message = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOrderPaymentDetails.pending, (state) => {
        state.status = GetOrderPaymentDetailsState.inProgress;
      })
      .addCase(getOrderPaymentDetails.fulfilled, (state, action) => {
        if (action.payload) {
          const { message, data } = action.payload;
          state.status = GetOrderPaymentDetailsState.success;
          state.message = message;
          state.data = data;
        }
      })
      .addCase(getOrderPaymentDetails.rejected, (state, action) => {
        state.status = GetOrderPaymentDetailsState.fail;
        state.message = action.payload as string;
        state.data = undefined;
      });
  },
});

export const selectGetOrderPaymentDetails = (state: RootState) =>
  state.getOrderPaymentDetails;
export const { resetGetOrderPaymentDetails } =
  getOrderPaymentDetailsSlice.actions;
export default getOrderPaymentDetailsSlice.reducer;
